import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { clientService } from '../../services/clientService';
import { Client } from '../../types/client';
import { useI18n } from '../../contexts/I18nContext';
import { isAbortError } from '../../utils/abortError';
import { clientAccessService } from '../../services/clientAccessService';

interface ClientsUserOverviewProps {
  onClientSelect: (client: Client) => void;
  refreshTrigger?: number;
  /**
   * Skip the picker when the caller belongs to exactly one client. A
   * ClientAdmin has a single organisation, so a one-row table is a click
   * that carries no decision.
   */
  autoSelectSingle?: boolean;
}

type SortField = 'name' | 'siren' | 'active' | 'users';

/**
 * Client-first entry point to user management: you pick the organisation
 * first, then work inside it. A flat list of every user across every client
 * is the wrong shape here — a technician belongs to one client, and a
 * ClientAdmin only ever manages their own.
 *
 * Rendered with the same table markup as the Clients screen so both lists
 * read identically.
 */
export const ClientsUserOverview: React.FC<ClientsUserOverviewProps> = ({
  onClientSelect,
  refreshTrigger,
  autoSelectSingle = false,
}) => {
  const { formatDate, t } = useI18n();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [userCounts, setUserCounts] = useState<Record<string, number>>({});

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // The API already scopes this to what the caller may see, so a
      // ClientAdmin lands on their own client and nothing else.
      const data = await clientService.fetchAllClients();
      setClients(data || []);
      // Counts arrive separately and never block the list: a slow or failing
      // count should not keep the clients from rendering.
      clientAccessService.fetchUserCounts().then(setUserCounts);
    } catch (err) {
      if (isAbortError(err)) return;
      console.error('[CLIENTS OVERVIEW] load failed', err);
      setError(t('clients.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients, refreshTrigger]);

  useEffect(() => {
    if (autoSelectSingle && !loading && clients.length === 1) {
      onClientSelect(clients[0]);
    }
    // onClientSelect identity changes on every parent render; tracking it
    // would re-fire the selection endlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSelectSingle, loading, clients]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const visible = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    const rows = needle
      ? clients.filter(
          c =>
            (c.name || '').toLowerCase().includes(needle) ||
            (c.siren || '').includes(needle),
        )
      : clients;

    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (sortField === 'active') {
        return ((a.active ? 1 : 0) - (b.active ? 1 : 0)) * dir;
      }
      if (sortField === 'users') {
        return ((userCounts[a.id] ?? 0) - (userCounts[b.id] ?? 0)) * dir;
      }
      const av = (sortField === 'siren' ? a.siren : a.name) || '';
      const bv = (sortField === 'siren' ? b.siren : b.name) || '';
      return av.localeCompare(bv) * dir;
    });
  }, [clients, searchTerm, sortField, sortDirection, userCounts]);

  const sortMark = (field: SortField) =>
    sortField === field ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : '';

  if (loading) {
    return (
      <div className="clients-table-loading" data-testid="clients-overview-loading">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="clients-table-error" data-testid="clients-overview-error">
        <div className="error-container">
          <h3>{t('clients.errorLoading')}</h3>
          <p>{error}</p>
          <button onClick={fetchClients}>{t('errors.retryAction')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="clients-table-container" data-testid="clients-overview">
      <div className="clients-table-header">
        <div className="table-controls">
          <div className="search-container">
            <input
              type="text"
              data-testid="clients-overview-search"
              placeholder={t('clients.searchPlaceholder')}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="search-input search-input--ota-style"
              aria-label={t('clients.searchPlaceholder')}
            />
          </div>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="clients-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('name')} className="sortable">
                {t('clients.clientName')}{sortMark('name')}
              </th>
              <th onClick={() => handleSort('siren')} className="sortable">
                {t('clients.sirenLabel')}{sortMark('siren')}
              </th>
              <th onClick={() => handleSort('users')} className="sortable">
                {t('users.title')}{sortMark('users')}
              </th>
              <th onClick={() => handleSort('active')} className="sortable">
                {t('clients.status')}{sortMark('active')}
              </th>
              <th>{t('clients.created')}</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={5} className="empty-state">
                  {t('users.noClientAvailable')}
                </td>
              </tr>
            ) : (
              visible.map(client => (
                <tr
                  key={client.id}
                  className="clickable-row"
                  data-testid={`client-row-${client.id}`}
                  onClick={() => onClientSelect(client)}
                >
                  <td>
                    <button
                      type="button"
                      className="client-name-link"
                      onClick={e => {
                        e.stopPropagation();
                        onClientSelect(client);
                      }}
                    >
                      {client.name}
                    </button>
                  </td>
                  <td className="monospace">{client.siren || '-'}</td>
                  <td className="numeric">{userCounts[client.id] ?? '—'}</td>
                  <td>
                    <span className={`status-badge ${client.active ? 'active' : 'inactive'}`}>
                      {client.active ? t('clients.active') : t('clients.inactive')}
                    </span>
                  </td>
                  <td>{client.created_at ? formatDate(new Date(client.created_at)) : '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
